/**
 * ErrorHandlerService
 *
 * Centralized error handling for the application.
 * Provides consistent user feedback through flash messages instead of browser alerts.
 */

export class ErrorHandlerService {
  /**
   * Display an error message to the user
   * Uses Turbo Streams to update the flash message area
   */
  static showError(message: string): void {
    this.showFlash(message, 'alert')
  }

  /**
   * Display a success message to the user
   * Uses Turbo Streams to update the flash message area
   */
  static showSuccess(message: string): void {
    this.showFlash(message, 'notice')
  }

  /**
   * Display a warning message to the user
   * Uses Turbo Streams to update the flash message area
   */
  static showWarning(message: string): void {
    this.showFlash(message, 'warning')
  }

  /**
   * Display a flash message by updating the flash element
   *
   * @param message - The message to display
   * @param type - The type of message ('alert', 'notice', 'warning')
   */
  private static showFlash(message: string, type: 'alert' | 'notice' | 'warning'): void {
    const flashElement = document.getElementById('flash')

    if (!flashElement) {
      // Fallback to console if flash element doesn't exist
      console.error('Flash element not found, message:', message)
      return
    }

    // Create the flash message HTML
    const flashHTML = `
      <div class="flash flash--${type}" role="alert" data-controller="removable" data-removable-delay-value="5000">
        <p>${this.escapeHtml(message)}</p>
        <button type="button" class="flash__close" data-action="removable#remove" aria-label="Close">×</button>
      </div>
    `

    // Update the flash container
    flashElement.innerHTML = flashHTML
  }

  /**
   * Escape HTML to prevent XSS attacks
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Handle an error with logging and user notification
   *
   * @param error - The error object
   * @param userMessage - Optional custom message for the user
   */
  static handleError(error: Error | unknown, userMessage?: string): void {
    // Log to console for debugging
    console.error('Application error:', error)

    // Show user-friendly message
    const message = userMessage || 'An unexpected error occurred. Please try again.'
    this.showError(message)
  }
}

// Export singleton instance
export const errorHandler = ErrorHandlerService
